import { useState } from 'react';
import { v4 as uuid } from 'uuid';

import { customFieldsApi, CustomFieldValuePatch } from 'apis/customFields';
import { emailsApi } from 'apis/emails';
import { usersApi } from 'apis/users';
import {
  NotificationType,
  useNotifications,
} from 'assets/hooks/useNotifications';
import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { notificationActions } from 'components/notifications/actions/notification';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import type { FormValues } from '../composeEmailModal.types';

type Email = FormValues['cc'][number];

const normalizeEmails = (emails: Email[]) =>
  emails.map((recipient) => ({
    ...recipient,
    email: recipient.email.trim().toLowerCase(),
  }));

type UseComposeEmailSubmitOptions = {
  deleteDraft: () => void;
  hide: () => void;
};

type UseComposeEmailSubmitResult = {
  onSubmit: (values: FormValues) => Promise<void>;
  sendFailed: boolean;
};

export function useComposeEmailSubmit({
  deleteDraft,
  hide,
}: UseComposeEmailSubmitOptions): UseComposeEmailSubmitResult {
  const [sendFailed, setSendFailed] = useState(false);
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const [createConversationEmail] =
    emailsApi.useCreateConversationEmailMutation();
  const [updateCustomFieldValue] = customFieldsApi.useUpdateValueMutation();

  const onSubmit = async (values: FormValues) => {
    setSendFailed(false);

    if (!values.fromEmail?.[0]) return;

    const toEmail = values.toEmail[0]?.email.trim().toLowerCase();
    if (!toEmail) return;

    const newContacts = [values.toEmail[0], ...values.cc, ...values.bcc].filter(
      (c) => c?.id === 'suggestion',
    );

    try {
      await Promise.all(
        newContacts.map((contact) =>
          dispatch(
            usersApi.endpoints.createUser.initiate(
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              {
                email: contact.email.trim().toLowerCase(),
                name: contact.name || contact.email,
                user_type: 'contact',
                phone_number: null,
                company_id: null,
              } as unknown as Parameters<
                typeof usersApi.endpoints.createUser.initiate
              >[0],
            ),
          ).unwrap(),
        ),
      );
    } catch {
      notify(
        {
          type: 'contactCreationFailed',
          text: `Could not create contact — message was not sent to ${toEmail}`,
          showDesktop: false,
        },
        NotificationType.Error,
      );
      return;
    }

    try {
      const result = await createConversationEmail({
        from_email: values.fromEmail[0],
        to_email: toEmail,
        subject: values.subject,
        body: values.body,
        content_type: 'html',
        ...(values.attachments_v2.length
          ? {
              attachments_v2: values.attachments_v2.map((a) => ({
                pretty_name: a.prettyName,
                url: a.url,
              })),
            }
          : {}),
        ...(values.cc.length ? { cc_emails: normalizeEmails(values.cc) } : {}),
        ...(values.bcc.length
          ? { bcc_emails: normalizeEmails(values.bcc) }
          : {}),
        ...(values.tags.length ? { tags: values.tags.map((t) => t.name) } : {}),
        ...(values.internalNote ? { internal_note: values.internalNote } : {}),
        ...(values.queueId?.length ? { queue_id: values.queueId[0] } : {}),
        ...(values.offerInQueue ? { offer: true } : {}),
        ...(values.parentConversation
          ? {
              link: {
                parent_csid: values.parentConversation,
                ...(values.linkType ? { link_type: values.linkType } : {}),
              },
            }
          : {}),
        ...(values.linkType === 'follow_up' && values.satisfactionLanguage
          ? { language: values.satisfactionLanguage }
          : {}),
      }).unwrap();

      const newCsid = result.data.csid;

      if (values.parentConversation) {
        dispatch(conversationActions.conversationLinkChild({ csid: newCsid }));
      }

      if (
        values.customAttributes &&
        Object.keys(values.customAttributes).length > 0
      ) {
        try {
          await updateCustomFieldValue({
            entityId: 'conversation',
            entityInstanceId: String(newCsid),
            patch: {
              before: {} as CustomFieldValuePatch,
              after:
                values.customAttributes as unknown as CustomFieldValuePatch,
            },
          });
        } catch {
          notify(
            {
              type: 'customAttributesSaveFailed',
              text: 'The email was sent, but custom attributes could not be saved.',
              showDesktop: false,
            },
            NotificationType.Error,
          );
        }
      }

      const notificationId = uuid();

      notify(
        {
          id: notificationId,
          type: 'messageSent',
          title: 'New message sent',
          text: 'Message was sent',
          time: 10000,
          showDesktop: false,
          leftAction: {
            text: 'Go to conversation',
            action: { navigate: `/conversation/${newCsid}` },
          },
          rightAction: {
            text: 'Dismiss',
            action: () => {
              dispatch(
                notificationActions.notificationRemoveById({
                  id: notificationId,
                }),
              );
            },
          },
        },
        NotificationType.Success,
      );

      deleteDraft();
      hide();
    } catch {
      setSendFailed(true);
      notify(
        {
          type: 'messageNotSent',
          text: `Some error occurred - message was not sent to ${toEmail}`,
          showDesktop: false,
        },
        NotificationType.Error,
      );
    }
  };

  return { onSubmit, sendFailed };
}
